import { BaseInfoData, useBaseInfoStore } from '@/pages/clusters/pages/Workload/create/base-info';
import { produce } from 'immer';
import partition from 'lodash/partition';
import { create } from 'zustand';
import { MetadataItem } from '../add-metadata';
import { ContainerItem } from '../container';
import { AnnotationMetadataItem, NodeSelectorItem } from '../nodes-and-metadate';
import { usePodNumsStore } from '../pod-nums/usePodNumsStore';
import { PodSchedulingValues } from '../pod-scheduling-rules/usePodSchedulingRulesStore';
import { PodSecurityContextData } from '../pod-security-context';
import { VolumeItem } from '../storage-settings';
import { ContainerPort, HeadlessServicePort, WorkloadType, WorkloadYaml } from '../types';
import { UpdateStrategyValues } from '../update-strategy';
import { useHeadlessServiceYamlStore } from './useHeadlessServiceYamlStore';
import { claimTemplatesToYaml, schedulingToYaml, strategyToYaml, volumesToYaml } from './utils';
import { containerToYaml } from './utils/yaml-container';

interface WorkloadYamlState {
  yamlData: WorkloadYaml;
}

const initWorkloadYaml: WorkloadYamlState = {
  yamlData: {
    apiVersion: 'apps/v1',
    kind: 'Deployment',
    metadata: {
      name: undefined,
      labels: {
        app: undefined,
      },
      annotations: {
        'stack.io/aliase': undefined,
        'stack.io/describe': undefined,
      },
    },
    spec: {
      replicas: 1,
      selector: {
        matchLabels: {
          app: undefined,
        },
      },
      template: {
        metadata: {
          labels: {
            app: undefined,
          },
        },
        spec: {
          containers: [],
          serviceAccount: 'default',
        },
      },
      strategy: {
        type: UpdateStrategyValues.ROLLING_UPDATE,
        rollingUpdate: {
          maxUnavailable: '25%',
          maxSurge: '25%',
        },
      },
    },
  },
};

interface WorkloadYamlAction {
  /** yaml->state: 用户更新 yaml 文件 */
  updateYamlData: (data: any) => void;
  /** statefulset 生成 serviceName */
  setYamlWorkloadServiceName: (name: string) => void;
  /** state->yaml: 更新基础信息的部分 */
  updateYamlBaseInfo: (baseInfo: BaseInfoData) => void;
  /** state->yaml: 容器组副本数量 */
  updateYamlReplicas: (replicas: number) => void;
  /** state->yaml: 更新容器设置 */
  updateYamlContainers: (containers: ContainerItem[]) => void;
  /** state->yaml: 更新策略 */
  updateYamlStrategy: (props: {
    strategy: UpdateStrategyValues;
    maxUnavailablePods?: string;
    maxExtraPods?: string;
    partition?: number;
  }) => void;
  /** state->yaml: statefulset 更新策略 */
  updateYamlUpdateStrategy: (props: {
    strategy: UpdateStrategyValues;
    partition?: number;
    maxUnavailablePods?: string;
  }) => void;
  /** state->yaml: daemonset 容器组就绪最短运行时长  */
  updateYamlMinReadySeconds: (seconds: number) => void;
  /** state->yaml: 容器组安全上下文 */
  updateYamlSecurityContext: (securityContext: PodSecurityContextData) => void;
  /** state->yaml: 调度规则 */
  updateYamlPodSchedulingRules: (rules: PodSchedulingValues) => void;
  /** state->yaml: 容器组添加元数据 */
  addYamlAnnotations: (metadata: MetadataItem[]) => void;
  /** state->yaml: 存储设置 */
  updateYamlStorageSettings: (storageSettings: VolumeItem[]) => void;
  /** state->yaml: 高级设置: 选择节点和添加元数据 */
  addYamlAnnotationsMetadatas: (
    annotationMetadata: AnnotationMetadataItem[],
    nodeSelector: NodeSelectorItem[],
  ) => void;
  /** 动态更新 kind 字段 */
  updateYamlKind: (kind: WorkloadType) => void;
  /** 重置 */
  resetYamlData: () => void;
}

export const useWorkloadYamlStore = create<WorkloadYamlState & WorkloadYamlAction>()((set, get) => {
  return {
    ...initWorkloadYaml,
    updateYamlData: (data) => {
      const updateBaseInfo = useBaseInfoStore.getState().updateBaseInfo;
      const updatePodNums = usePodNumsStore.getState().updatePodNums;
      // const updateContainerArray = useContainerStore.getState().updateContainerArray;
      // 如果用户手动清空了 yaml ,那么 yamlData = undefined
      set({ yamlData: data });
      // 当手动修改了 yamlData 时,需要将数据同步到其他 store 中
      updateBaseInfo({
        name: data?.metadata.name,
        namespace: data?.metadata?.namespace,
        alias: data?.metadata.annotations?.['stack.io/aliase'],
        annotations: data?.metadata.annotations?.['stack.io/describe'],
      });
      updatePodNums(data?.spec.replicas);
      /** 修改 yaml 同步到 store 中 */
    },
    setYamlWorkloadServiceName: (name) => {
      /**
       * 这里生成的 serviceName 是为了给 statefulset 使用的
       * 这里的 serviceName 是为了给 headless service 使用的
       * serviceName 与 headless service 的名称一致
       */
      if (!name) return;
      // const serviceName = genId({ prefix: formatName(name), length: 4, separator: '-' });
      const serviceName = name;
      set(
        produce((state: WorkloadYamlState) => {
          state.yamlData.spec.serviceName = serviceName;
        }),
      );
      useHeadlessServiceYamlStore.getState().setYamlServiceName(serviceName);
      // 这里的格式化是为了避免 headless service 的名称以数字开头
      // function formatName(name) {
      //   return /^\d/.test(name) ? `s${name}` : name;
      // }
    },
    updateYamlBaseInfo: (baseInfo) =>
      set(
        produce((state: WorkloadYamlState) => {
          state.yamlData.metadata.name = baseInfo.name;
          state.yamlData.metadata.labels.app = baseInfo.name;
          state.yamlData.metadata.namespace = baseInfo.namespace;
          state.yamlData.metadata.annotations = {
            ...state.yamlData.metadata.annotations, // 对 annotations 补充键值对
            'stack.io/describe': baseInfo.annotations,
            'stack.io/aliase': baseInfo.alias,
          };
          state.yamlData.spec.selector.matchLabels.app = baseInfo.name;
          state.yamlData.spec.template.metadata.labels.app = baseInfo.name;
        }),
      ),
    updateYamlReplicas: (replicas: number) =>
      set(
        produce((state) => {
          state.yamlData.spec.replicas = replicas;
        }),
      ),
    updateYamlContainers: (containers) => {
      const [initContainers, workContainers] = partition(
        containers,
        (container: ContainerItem) => container.containerSetting.containerType === 'initContainers',
      );
      set(
        produce((state: WorkloadYamlState) => {
          state.yamlData.spec.template.spec.initContainers = initContainers.map(
            (item: ContainerItem) => {
              const initContainerItem = containerToYaml(item);
              return initContainerItem;
            },
          );
          state.yamlData.spec.template.spec.containers = workContainers.map(
            (item: ContainerItem) => {
              const workContainerItem = containerToYaml(item);
              return workContainerItem;
            },
          );
        }),
      );
      /** 映射工作容器的端口 */
      const ports = workContainers.map((item: ContainerItem) => item.portSetting);
      const flatPorts: HeadlessServicePort[] = ports
        .flat()
        .filter(Boolean)
        .map((item: ContainerPort) => {
          return {
            name: item.name,
            protocol: item.protocol,
            port: item.servicePort,
            targetPort: item.containerPort,
          };
        });
      useHeadlessServiceYamlStore.getState().updateYamlServicePort(flatPorts);
    },
    // deployment(无状态副本集) 需要 strategy
    updateYamlStrategy: ({ strategy, maxUnavailablePods, maxExtraPods, partition }) => {
      set(
        produce((state: WorkloadYamlState) => {
          const res = strategyToYaml({
            strategy,
            maxUnavailablePods,
            maxExtraPods,
            partition,
          });
          state.yamlData.spec.strategy = res;
        }),
      );
    },
    // statefulset(有状态副本集) 需要 updateStrategy
    updateYamlUpdateStrategy: ({ strategy, partition, maxUnavailablePods }) => {
      set(
        produce((state: WorkloadYamlState) => {
          const res = strategyToYaml({
            strategy,
            partition,
            maxUnavailablePods,
          });
          delete state.yamlData.spec.strategy; // statefulset 不需要 strategy 而是 updateStrategy
          state.yamlData.spec.updateStrategy = res;
        }),
      );
    },
    updateYamlMinReadySeconds: (seconds) => {
      set(
        produce((state: WorkloadYamlState) => {
          state.yamlData.spec.minReadySeconds = seconds;
        }),
      );
    },
    updateYamlSecurityContext: (securityContext) => {
      set(
        produce((state: WorkloadYamlState) => {
          state.yamlData.spec.template.spec.securityContext = securityContext;
        }),
      );
    },
    addYamlAnnotations: (metadata) => {
      set(
        produce((state: WorkloadYamlState) => {
          if (!state.yamlData.spec.template.metadata.annotations && metadata.length) {
            // 确保 annotations 对象存在
            state.yamlData.spec.template.metadata.annotations = {};
          }
          metadata.forEach((item) => {
            state.yamlData.spec.template.metadata.annotations[item.key] = item.value;
          });
        }),
      );
    },
    updateYamlPodSchedulingRules: (rules) => {
      set(
        produce((state: WorkloadYamlState) => {
          state.yamlData.spec.template.spec.affinity = schedulingToYaml(
            rules,
            state.yamlData.metadata.labels,
          );
        }),
      );
    },
    updateYamlStorageSettings: (storageSettings) => {
      set(
        produce((state: WorkloadYamlState) => {
          state.yamlData.spec.template.spec.volumes = volumesToYaml(storageSettings);
          state.yamlData.spec.volumeClaimTemplates = claimTemplatesToYaml(
            storageSettings,
            state.yamlData.metadata.namespace,
          );
        }),
      );
    },
    addYamlAnnotationsMetadatas: (annotationMetadata, nodeSelector) => {
      set(
        produce((state: WorkloadYamlState) => {
          const preserved = {
            'stack.io/aliase': state.yamlData.metadata.annotations?.['stack.io/aliase'],
            'stack.io/describe': state.yamlData.metadata.annotations?.['stack.io/describe'],
          };
          const newAnnotations = annotationMetadata.reduce((acc, item) => {
            if (item.key !== 'stack.io/aliase' && item.key !== 'stack.io/describe') {
              acc[item.key] = item.value;
            }
            return acc;
          }, {});
          state.yamlData.metadata.annotations = { ...newAnnotations, ...preserved };
          state.yamlData.spec.template.spec.nodeSelector = nodeSelector.reduce((acc, item) => {
            acc[item.key] = item.value;
            return acc;
          }, {});
        }),
      );
    },
    updateYamlKind: (kind) => {
      set(
        produce((state: WorkloadYamlState) => {
          const { DEPLOYMENT, STATEFULSET, DAEMONSET } = WorkloadType;
          switch (kind) {
            case DAEMONSET:
              state.yamlData.kind = 'DaemonSet';
              break;
            case STATEFULSET:
              state.yamlData.kind = 'StatefulSet';
              break;
            case DEPLOYMENT:
            default:
              state.yamlData.kind = 'Deployment';
              break;
          }
        }),
      );
    },
    resetYamlData: () => set(initWorkloadYaml),
  };
});
